/**
 * **The completion ring on a session card: whether it is drawn at all, and what colour it is.**
 *
 * Two product decisions, both kept out of JSX so a test can reach them. The bands are
 * `docs/design/README.md`'s entry for `2026-09-16-karty-feedbacku.html`: **≥90 good, ≥60 warn,
 * below that danger, closed at the top.** What leaves here is a NAME — `model/` decides and the
 * stylesheet paints.
 *
 * ── "No sets logged" is not "nothing was done" ───────────────────────────────────────────────
 * The queue's `sessionSetsDone` / `sessionSetsTotal` come from a grouped read that omits a session
 * with no logged sets entirely, so both arrive `null` — an ABSENCE, not a zero. A ring at 0% would
 * say the client finished none of twenty-four prescribed sets, which is a claim nobody made.
 *
 * A real `0` with a real denominator IS drawn, at 0% and in `danger`: every prescribed set skipped
 * is a fact the trainer should see, and a different fact from a session the read could not count.
 */

import { clampPercent } from '~/shared/lib/percent';

/** Which band a completion percentage falls in. A name, not a colour — the themes differ. */
export type RingInk = 'good' | 'warn' | 'danger';

/** The band boundaries, closed at the top — 90 is good, 89.9 is not; 60 is warn, 59.9 is not. */
const GOOD_FROM = 90;
const WARN_FROM = 60;

export function ringInk(percent: number): RingInk {
  if (percent >= GOOD_FROM) return 'good';
  if (percent >= WARN_FROM) return 'warn';
  return 'danger';
}

/** A ring the card can draw: the fraction behind it and the band it falls in. */
export interface SessionRing {
  readonly done: number;
  readonly total: number;
  /** Rounded and clamped to 0–100, because it is also an arc length and an `aria-valuenow`. */
  readonly percent: number;
  readonly ink: RingInk;
}

/**
 * The ring for one session, or `null` where there is nothing honest to draw.
 *
 * `null` when either counter is absent, and when the denominator is not positive: `0 / 0` is a
 * session with no counted sets, and dividing it produces `NaN`, not a percentage.
 */
export function sessionRing(
  setsDone: number | null | undefined,
  setsTotal: number | null | undefined,
): SessionRing | null {
  if (typeof setsDone !== 'number' || typeof setsTotal !== 'number') return null;
  if (!Number.isFinite(setsDone) || !Number.isFinite(setsTotal)) return null;
  if (setsTotal <= 0) return null;

  const percent = clampPercent((setsDone / setsTotal) * 100);
  if (percent === null) return null;

  return { done: setsDone, total: setsTotal, percent, ink: ringInk(percent) };
}
